import { useCanvasStore } from "./canvas-store";
import { useShallow } from "zustand/react/shallow";
import type {
  CanvasChatMessage,
  CanvasImage,
  PlaybookProgressState,
  CanvasPlaceholder,
  CanvasGenerationRow,
  CanvasView,
  WorkflowState,
} from "./canvas-types";
import type { AdCopyVariation } from "./canvas-store";

/* ──────────────────────────────────────────────────────────────────────────
 * Active-project selectors. Components use these instead of direct store
 * field access. Reads from state.projects[activeId]; the old flat fields
 * have been removed in Task 11.
 *
 * IMPORTANT: every selector below MUST return a stable reference for empty
 * fallbacks. Returning a fresh [] or {} literal each call would cause
 * useSyncExternalStore to detect a change every render → infinite loop.
 * That's why we hoist these constants to module scope and reuse them.
 * ────────────────────────────────────────────────────────────────────────── */

const EMPTY_MESSAGES: CanvasChatMessage[] = [];
const EMPTY_IMAGES: CanvasImage[] = [];
const EMPTY_QA_BADGES: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }> = {};
const EMPTY_PLACEHOLDERS: Record<string, CanvasPlaceholder> = {};
const EMPTY_GENERATION_ROWS: CanvasGenerationRow[] = [];
const EMPTY_AD_COPY: Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }> = {};
const EMPTY_REQUEST_ID_MAP: Record<string, string> = {};
const EMPTY_CONVERSATION_HISTORY: Array<{ role: 'user' | 'assistant'; content: string }> = [];

export function useActiveProjectId(): number | null {
  return useCanvasStore((s) => s.activeProjectId);
}

export function useActiveProjectMessages(): CanvasChatMessage[] {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_MESSAGES;
    return s.projects[id]?.messages ?? EMPTY_MESSAGES;
  });
}

export function useActiveProjectImages(): CanvasImage[] {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_IMAGES;
    return s.projects[id]?.images ?? EMPTY_IMAGES;
  });
}

export function useActiveProjectIsTyping(): boolean {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].isTyping;
    return Boolean(
      s.typingByProject[id ?? "new"] ||
        s.typingByProject["new"]
    );
  });
}

export function useActiveProjectAgentThinking(): string | null {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].agentThinking;
    return null;
  });
}

export function useActiveProjectPlaybookProgress(): PlaybookProgressState | null {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].playbookProgress;
    return null;
  });
}

export function useActiveProjectQaBadges(): Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }> {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_QA_BADGES;
    return s.projects[id]?.imageQaBadges ?? EMPTY_QA_BADGES;
  });
}

export function useActiveProjectIsCanvasOpen(): boolean {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].isCanvasOpen;
    return false;
  });
}

export function useActiveProjectActiveView(): CanvasView {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].activeView;
    // No project active — fall back to the standalone view slot. Sidebar
    // menu items (Heartbeat, Brands, Analytics, etc.) write here via
    // setActiveView when they clear activeProjectId.
    return s.standaloneActiveView;
  });
}

export function useActiveProjectCanvasPlaceholders(): Record<string, CanvasPlaceholder> {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_PLACEHOLDERS;
    return s.projects[id]?.canvasPlaceholders ?? EMPTY_PLACEHOLDERS;
  });
}

export function useActiveProjectGenerationRows(): CanvasGenerationRow[] {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_GENERATION_ROWS;
    return s.projects[id]?.generationRows ?? EMPTY_GENERATION_ROWS;
  });
}

export function useActiveProjectWorkflowState(): WorkflowState | null {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id != null && s.projects[id]) return s.projects[id].workflowState;
    return null;
  });
}

export function useActiveProjectCanvasAdCopy(): Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }> {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_AD_COPY;
    return s.projects[id]?.canvasAdCopy ?? EMPTY_AD_COPY;
  });
}

export function useActiveProjectImageRequestIdToUrl(): Record<string, string> {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_REQUEST_ID_MAP;
    return s.projects[id]?.imageRequestIdToUrl ?? EMPTY_REQUEST_ID_MAP;
  });
}

export function useActiveProjectConversationHistory(): Array<{ role: 'user' | 'assistant'; content: string }> {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    if (id == null) return EMPTY_CONVERSATION_HISTORY;
    return s.projects[id]?.conversationHistory ?? EMPTY_CONVERSATION_HISTORY;
  });
}

export function useActiveProjectHasPersistedImages(): boolean {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    return id != null ? (s.projects[id]?.hasPersistedImages ?? false) : false;
  });
}

export function useActiveProjectPendingPanTarget(): { x: number; y: number } | null {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    return id != null ? (s.projects[id]?.pendingPanTarget ?? null) : null;
  });
}

export function useActiveProjectPendingFitToView(): boolean {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    return id != null ? (s.projects[id]?.pendingFitToView ?? false) : false;
  });
}

export function useActiveProjectPendingFitToRow(): { minX: number; minY: number; maxX: number; maxY: number } | null {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    return id != null ? (s.projects[id]?.pendingFitToRow ?? null) : null;
  });
}

export function useActiveProjectLoading(): boolean {
  return useCanvasStore((s) => {
    const id = s.activeProjectId;
    return id != null ? (s.projects[id]?.loading ?? false) : false;
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Cross-project selectors (sidebar, "all chats" views).
 *
 * useShallow does shallow array/object comparison so the selector only
 * triggers a re-render when the derived list actually changes — the
 * Object.values(...).map(...) recomputes on every store change but
 * useShallow short-circuits when the new array is structurally equal
 * to the previous one.
 * ────────────────────────────────────────────────────────────────────────── */

export function useAllLoadedProjects(): Array<{ projectId: number; messageCount: number; imageCount: number }> {
  return useCanvasStore(
    useShallow((s) =>
      Object.values(s.projects).map((p) => ({
        projectId: p.projectId,
        messageCount: p.messages.length,
        imageCount: p.images.length,
      })),
    ),
  );
}
